import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Tooltip,
  BarChart,
  Bar,
  CartesianGrid,
  YAxis,
  XAxis,
  Legend,
} from 'recharts';
import { useDashboardAnalytics } from 'frontend/src/contexts/DashboardAnalytics';
import { CircularProgress } from '@material-ui/core';
import { useTheme } from '@material-ui/styles';
import { firstWordUppercase, providerName } from '../../../../utils';
import { RechartsTooltipProps } from '../types';
import Card from '../../Card';
import makeStyles from '@material-ui/core/styles/makeStyles';
import InfoIconWithTooltip from '../../../BuildingBlocks/InfoIconWithTooltip';
import CogTooltip from './CogTooltip';

const useStyles = makeStyles((theme: any) => ({
  cornerIcon: {
    position: 'absolute',
    top: theme.spacing(2.5),
    right: theme.spacing(2.25),
  },
  chartContainer: {
    width: '100%',
    height: '100%',
    display: 'flex',
    position: 'relative',
  },
}));

export default function CostOfGoodsChart() {
  const classes = useStyles({});
  const ref = useRef<HTMLDivElement>();
  const [graphContainerWidth, setGraphContainerWidth] = useState(0);
  const theme: any = useTheme();
  const { analytics, isLoading } = useDashboardAnalytics();
  const { costOfGoods = {}, costOfGoodBenchmark = {} } = analytics;

  let cogChartData = {};
  for (const [stageName, amount] of Object.entries(costOfGoodBenchmark)) {
    cogChartData[stageName] = { name: stageName, benchmark: amount };
  }
  for (const [stageName, amount] of Object.entries(costOfGoods)) {
    cogChartData[stageName]['company'] = amount;
  }

  const updateWidth = () =>
    setGraphContainerWidth(ref.current?.clientWidth ?? 0);

  useLayoutEffect(() => updateWidth());

  useEffect(() => {
    window.addEventListener('resize', updateWidth);
    return () => {
      window.removeEventListener('resize', updateWidth);
    };
  }, []);

  return (
    <Card title={'My Cost of Goods Sold'} noPadding={true}>
      <div className={classes.cornerIcon}>
        <InfoIconWithTooltip
          helpText={`Compare the costs of your supply chain stages to the average costs for ${providerName}'s clients.`}
        />
      </div>
      {isLoading && <CircularProgress />}
      {!isLoading && (
        <div className={classes.chartContainer} ref={ref}>
          <BarChart
            width={graphContainerWidth - 40}
            height={350}
            layout="vertical"
            data={Object.values(cogChartData)}
          >
            <CartesianGrid horizontal={false} strokeDasharray="3" />
            <XAxis type="number" tickFormatter={(value) => `${value}%`} />
            <YAxis
              width={65}
              type="category"
              dataKey="name"
              tickFormatter={(value: string) => firstWordUppercase(value)}
            />
            <Tooltip
              content={(props: RechartsTooltipProps) => (
                <CogTooltip {...props} />
              )}
            />
            <Legend align="right" />
            <Bar
              name="Benchmark"
              barSize={8}
              dataKey="benchmark"
              fill={theme.palette.brand.greenLightAnalytic}
            />
            <Bar
              name="Your Spend"
              barSize={8}
              dataKey="company"
              fill={theme.palette.brand.blueDeepSeaAnalytic}
            />
          </BarChart>
        </div>
      )}
    </Card>
  );
}
